import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@components/atoms/modals/modal.tsx';
import { CustomModelGuideModal } from '@components/custom-models/custom-model-guide-modal.tsx';
import { CaptionAddToTrainingPanel } from '@components/editors/caption-editor/body/caption-add-to-training-panel.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';

const OpenTrainingDialog = createContext<(segmentId: number) => void>(
  () => undefined
);

/** Opens the "Add to training data" dialog for a caption segment. */
export const useOpenTrainingDialog = () => useContext(OpenTrainingDialog);

/**
 * Owns the caption editor's "Add to training data" dialog, once, outside the
 * segment rows.
 *
 * It used to render inside each row's tools column, which sits in the row's
 * Slate editor. That column is hidden whenever the editor loses focus, and
 * opening the dialog moves focus into it, so on the first click the dialog
 * opened invisible. The column also cancels every mouse-down, which reached
 * the dialog's text boxes, and the virtualised list may rebuild a row and
 * drop its state. Out here none of that applies.
 */
export const CaptionTrainingDialogProvider = ({
  filteredItems,
  children
}: PropsWithChildren<{ filteredItems: DocumentSubtitleSegmentV1[] }>) => {
  const { t } = useTranslation();
  const { document: captionDoc } = useCaption();
  const [segmentId, setSegmentId] = useState<number | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const close = useCallback(() => setSegmentId(null), []);

  const segment =
    segmentId === null
      ? undefined
      : captionDoc?.document.find(s => s.id === segmentId);

  return (
    <OpenTrainingDialog.Provider value={setSegmentId}>
      {children}
      <Modal
        isOpen={!!segment}
        onClose={close}
        title={t('add-to-training-data')}
        size="medium"
        customWidth="min(440px, 100%)"
        customMaxHeight="90vh"
      >
        {segment && (
          <CaptionAddToTrainingPanel
            key={segment.id}
            segment={segment}
            filteredItems={filteredItems}
            onClose={close}
            onSubmitted={({ isFirstContribution }) => {
              // Close on success and, for a first-time contributor, show
              // the guide. It lives here too, so it survives the close.
              close();
              if (isFirstContribution) setShowGuide(true);
            }}
          />
        )}
      </Modal>
      {showGuide && (
        <CustomModelGuideModal
          isOpen={showGuide}
          onClose={() => setShowGuide(false)}
          contributionOnly
        />
      )}
    </OpenTrainingDialog.Provider>
  );
};
